import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `heroB2B` block.
 *
 * Field tree mirrors the CMS-authorable structural copy in
 * `./hero-b2b.tsx` (headline, CTAs, eyebrow, extra-content row). The
 * B2BLeftIllustrations + B2BRightIllustrations + grid-pattern overlay are
 * HARDCODED in the React component because they're large bespoke SVG
 * decorations -- not content a CMS author should be editing.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `hero-b2b.tsx` / `hero-b2b.payload.ts`
 *   React component export:  `HeroB2B`
 *   Payload `slug`:          `heroB2B`
 *   Payload `interfaceName`: `HeroB2BBlock`
 */

export const HeroB2BBlock: BlockConfig = {
  slug: 'heroB2B',
  interfaceName: 'HeroB2BBlock',
  labels: {
    singular: 'B2B Hero',
    plural: 'B2B Hero Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge text',
    },
    {
      ...iconField({
        name: 'eyebrowIcon',
        label: 'Eyebrow icon (lucide-react name)',
      }),
      admin: {
        description:
          'Optional icon rendered inside the pill-variant eyebrow badge. Supported: Shield, Lock, Zap, Sparkles, Star, Bell.',
      },
    },
    {
      name: 'eyebrowVariant',
      type: 'select',
      defaultValue: 'pill',
      label: 'Eyebrow style',
      options: [
        { label: 'Pill (outlined badge with icon)', value: 'pill' },
        { label: 'Section badge', value: 'badge' },
      ],
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'headlineEmphasis',
      type: 'text',
      label: 'Headline emphasis',
      admin: {
        description:
          'Optional phrase rendered in the primary accent color after the headline. Example: "Modern Enterprise" on /b2b.',
      },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 3 },
    },
    {
      name: 'primaryCta',
      type: 'group',
      label: 'Primary CTA',
      fields: [
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button URL' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        iconField({
          name: 'iconRight',
          label: 'Right-side icon (lucide-react name)',
        }),
        {
          name: 'variant',
          type: 'select',
          defaultValue: 'default',
          label: 'Button variant',
          options: [
            { label: 'Default (filled)', value: 'default' },
            { label: 'Outline', value: 'outline' },
            { label: 'Ghost', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'secondaryCta',
      type: 'group',
      label: 'Secondary CTA',
      fields: [
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button URL' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        iconField({
          name: 'iconRight',
          label: 'Right-side icon (lucide-react name)',
        }),
        {
          name: 'variant',
          type: 'select',
          defaultValue: 'outline',
          label: 'Button variant',
          options: [
            { label: 'Default (filled)', value: 'default' },
            { label: 'Outline', value: 'outline' },
            { label: 'Ghost', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'extraContent',
      type: 'array',
      label: 'Trust indicators row (icon + text)',
      labels: { singular: 'Item', plural: 'Items' },
      maxRows: 5,
      fields: [
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
        }),
        { name: 'text', type: 'text', required: true, label: 'Text' },
      ],
    },
  ],
};

export default HeroB2BBlock;
